import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import pagesApi from "../admincomponents/PagesApi";
import { resolveAssetUrl } from "../baseurl";
import { BASE_URL } from "../baseurl";

const PAGE_NAMES = { home: "Home", shop: "Shop", faq: "FAQ", contact: "Contact" };
const SERVER_ORIGIN = BASE_URL.replace(/\/api\/?$/, "");

const TEXT_FIELDS = {
  home: [
    ["heroTitle", "Hero title", "input"],
    ["heroDescription", "Hero description", "textarea"],
    ["heroButtonText", "Hero button text", "input"],
    ["researchNoticeTitle", "Research notice title", "input"],
    ["researchNoticeText", "Research notice text", "textarea"],
    ["featuredProductsTitle", "Featured products title", "input"],
    ["faqTitle", "Home FAQ title", "input"],
  ],
  shop: [["shopTitle", "Shop title", "input"]],
  faq: [["faqPageTitle", "FAQ page title", "input"]],
  contact: [
    ["firstNameLabel", "First name label", "input"],
    ["lastNameLabel", "Last name label", "input"],
    ["emailLabel", "Email label", "input"],
    ["messageLabel", "Message label", "input"],
    ["submitLabel", "Submit button text", "input"],
  ],
};

function Field({ label, value, onChange, multiline }) {
  const common = {
    value: value || "",
    onChange: (event) => onChange(event.target.value),
    className: "w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-500",
  };
  return (
    <label className="flex flex-col gap-2">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      {multiline ? <textarea {...common} rows={4} /> : <input {...common} />}
    </label>
  );
}

function ImageField({ value, onChange }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(value || "");

  useEffect(() => setPreview(value || ""), [value]);
  const previewUrl = resolveAssetUrl(preview);

  const chooseFile = (event) => {
    const file = event.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result);
      onChange(reader.result);
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  return (
    <div className="flex flex-col gap-3">
      <span className="text-sm font-medium text-gray-700">Shop banner image</span>
      {preview && <img src={previewUrl} alt="Current shop banner" className="w-full max-w-xl h-40 object-cover rounded-md border border-gray-200" />}
      <input ref={inputRef} type="file" accept="image/*" onChange={chooseFile} className="hidden" />
      <button type="button" onClick={() => inputRef.current?.click()} className="self-start border border-gray-300 text-gray-700 text-sm font-medium px-4 py-2 rounded-md hover:border-teal-500 hover:text-teal-600">
        {preview ? "Replace image" : "Choose image"}
      </button>
    </div>
  );
}

function VideoField({ value, onChange }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(value || "");

  useEffect(() => setPreview(value || ""), [value]);

  const previewUrl = resolveAssetUrl(preview);

  const chooseFile = (event) => {
    const file = event.target.files?.[0];
    if (!file || !file.type.startsWith("video/")) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result);
      onChange(reader.result);
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  return (
    <div className="flex flex-col gap-3">
      <span className="text-sm font-medium text-gray-700">Home banner video</span>
      {preview && (
        <video
          src={previewUrl}
          controls
          muted
          className="w-full max-w-xl h-48 object-cover rounded-md border border-gray-200 bg-black"
        />
      )}
      <input ref={inputRef} type="file" accept="video/mp4,video/webm,video/quicktime,video/*" onChange={chooseFile} className="hidden" />
      <button type="button" onClick={() => inputRef.current?.click()} className="self-start border border-gray-300 text-gray-700 text-sm font-medium px-4 py-2 rounded-md hover:border-teal-500 hover:text-teal-600">
        {preview ? "Replace video" : "Choose video"}
      </button>
      <p className="text-xs text-gray-400">MP4 or WebM recommended. Select a file from your computer; no URL is required.</p>
    </div>
  );
}

function RepeatingFields({ label, items, onChange, kind }) {
  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-gray-800 font-semibold text-base">{label}</h2>
      {items.map((item, index) => (
        <div key={index} className="border border-gray-200 rounded-md p-4 flex flex-col gap-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Item {index + 1}</p>
          <Field label={kind === "features" ? "Title" : "Question"} value={kind === "features" ? item.title : item.question} onChange={(value) => onChange(index, kind === "features" ? { ...item, title: value } : { ...item, question: value })} />
          <Field label={kind === "features" ? "Description" : "Answer"} value={kind === "features" ? item.text : item.answer} multiline onChange={(value) => onChange(index, kind === "features" ? { ...item, text: value } : { ...item, answer: value })} />
        </div>
      ))}
    </div>
  );
}

export default function FixedPageEditor() {
  const { slug: routeSlug } = useParams();
  const slug = routeSlug || "home";
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    pagesApi.get(slug).then((page) => {
      if (!cancelled) setContent(page);
    }).catch(() => {
      if (!cancelled) setError("Couldn't load this page content.");
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [slug]);

  const update = (key, value) => setContent((current) => ({ ...current, [key]: value }));
  const updateItem = (key, index, value) => setContent((current) => ({ ...current, [key]: current[key].map((item, itemIndex) => itemIndex === index ? value : item) }));

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    setError("");
    try {
      await pagesApi.update(slug, content);
      setMessage("Saved. The storefront now uses this content.");
    } catch (err) {
      setError(err.response?.data?.message || "Couldn't save this page.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-gray-400 text-sm">Loading page content...</div>;
  if (!content) return <div className="text-red-500 text-sm">{error || "Page not found."}</div>;

  return (
    <form onSubmit={save} className="w-full max-w-4xl">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <button type="button" onClick={() => navigate("/admin/pages")} className="text-teal-600 text-sm font-medium mb-3">← All pages</button>
          <h1 className="text-gray-800 font-semibold text-2xl">Manage {PAGE_NAMES[slug] || content.pageName}</h1>
          <p className="text-gray-500 text-sm mt-1">Edit content only. Page structure and section order are fixed.</p>
        </div>
        <button type="submit" disabled={saving} className="bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white text-sm font-medium px-5 py-2.5 rounded-md">
          {saving ? "Saving..." : "Save changes"}
        </button>
      </div>

      {message && <div className="mb-5 bg-teal-50 border border-teal-100 text-teal-700 text-sm rounded-md px-4 py-3">{message}</div>}
      {error && <div className="mb-5 bg-red-50 border border-red-100 text-red-600 text-sm rounded-md px-4 py-3">{error}</div>}

      <div className="bg-white border border-gray-200 rounded-lg p-6 flex flex-col gap-7">
        {(TEXT_FIELDS[slug] || []).map(([key, label, type]) => (
          <Field key={key} label={label} value={content[key]} multiline={type === "textarea"} onChange={(value) => update(key, value)} />
        ))}
        {slug === "shop" && <ImageField value={content.shopBannerImage} onChange={(value) => update("shopBannerImage", value)} />}
        {slug === "home" && (
          <>
            <VideoField value={content.heroVideo || "/videos/hero-video.mp4"} onChange={(value) => update("heroVideo", value)} />
            <RepeatingFields label="Trust features (3 fixed items)" kind="features" items={content.features || []} onChange={(index, value) => updateItem("features", index, value)} />
            <RepeatingFields label="Homepage FAQ (4 fixed items)" kind="faq" items={content.faqItems || []} onChange={(index, value) => updateItem("faqItems", index, value)} />
          </>
        )}
        {slug === "faq" && <RepeatingFields label="FAQ items (7 fixed items)" kind="faq" items={content.faqItems || []} onChange={(index, value) => updateItem("faqItems", index, value)} />}
      </div>
    </form>
  );
}